/**
 * USERS & TEAM SETTINGS (TS-33) — Applicazione "Utenti e team" del pannello
 * impostazioni: gestione degli utenti e loro suddivisione in team.
 *
 * Tab orizzontali: Utenti (default) · Team.
 *
 * STATO: lista reale (getGroupMembers), creazione e modifica utente funzionanti
 * via endpoint upsert (POST /users): la creazione genera la password temporanea
 * e invia la mail di registrazione; la modifica aggiorna nome/email/ruolo. Creazione
 * e modifica sono riservate agli amministratori CRM (bottone e azione riga visibili
 * solo agli admin; enforce reale lato server). "Esporta tutti gli utenti" genera il
 * CSV dalla lista e lo scarica. Il tab Team è ancora uno stub ("coming soon").
 *
 * Volutamente NON replicato dalla UI di riferimento: inviti/viste e filtri
 * avanzati. Qui è un CRUD classico con lista in stile search container.
 */

import { useState, useEffect, useMemo, useCallback, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { triggerBrowserDownload } from '@crm/shared';
import { resolvePortraitURL } from '../../api/portrait';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { useAppSelector } from '../../store/hooks';
import { extractError } from '../../store/thunkHelpers';
import { userApi, type Member } from '../../entities/user/userApi';
import { teamApi, type Team } from '../../entities/team/teamApi';
import { ConfirmDialog } from '../ConfirmDialog';
import { useConfirmDialog } from '../useConfirmDialog';
import { useToast } from '../useToast';

type UserRole = 'admin' | 'standard';

// Stato Liferay dell'utente (WorkflowConstants): INACTIVE = disattivato, tutto il resto attivo.
const USER_STATUS_APPROVED = 0;
const USER_STATUS_INACTIVE = 5;

/** Escape di una cella CSV: racchiude tra virgolette se contiene separatori. */
function csvCell(value: string): string {
  const s = String(value ?? '');
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function UsersTeamSettings() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'users' | 'team'>('users');

  return (
    <div className="users-settings">
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as 'users' | 'team')}
        className="account-settings__tabs"
      >
        <Tab value="users" label={t('userSettings.usersTeam.tabs.users')} className="tab-compact" />
        <Tab value="team" label={t('userSettings.usersTeam.tabs.team')} className="tab-compact" />
      </Tabs>

      {tab === 'users' ? <UsersTab /> : <TeamsTab />}
    </div>
  );
}

// ── Tab Utenti (lista search container + aggiungi + azioni) ─────────────────
function UsersTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const groupId = useCurrentGroupId();
  // "Aggiungi utente" è riservato agli amministratori CRM: lo stato viene dalla
  // sessione (User.administrator), non dedotto dalla lista (enforce reale lato server).
  const isAdmin = useAppSelector((s) => s.auth.user?.administrator) ?? false;
  // Id dell'utente loggato: la disattivazione di sé stesso è vietata (nascondiamo il toggle).
  const currentUserId = useAppSelector((s) => s.auth.user?.userId);
  const confirm = useConfirmDialog();
  const [search, setSearch] = useState('');
  // null = modale chiusa; { member: null } = creazione; { member } = modifica.
  const [modal, setModal] = useState<{ member: Member | null } | null>(null);
  const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null);
  const [users, setUsers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);

  // Guardia anti-setState-dopo-unmount (es. cambio tab durante il fetch).
  // Reset a true sul mount per sopravvivere al doppio-invoke di StrictMode.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Lista utenti reale del sito (Member con portraitURL + ruolo CRM).
  const loadMembers = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const members = await userApi.getGroupMembers(groupId);
      if (mountedRef.current) setUsers(members);
    } catch (err) {
      if (mountedRef.current) toast({ severity: 'error', message: extractError(err) });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [groupId, toast]);

  useEffect(() => { void loadMembers(); }, [loadMembers]);

  // Attiva/disattiva un utente e ricarica la lista. Lo stato target è l'opposto
  // dell'attuale (toggle); il ruolo/nome può comparire negli autocomplete -> invalido la cache.
  const handleToggleStatus = useCallback(async (m: Member) => {
    const target = m.status === USER_STATUS_INACTIVE ? USER_STATUS_APPROVED : USER_STATUS_INACTIVE;
    try {
      await userApi.updateStatus(m.userId, target);
      userApi.invalidateCache(groupId);
      toast({ severity: 'success', message: t('common.saved') });
      void loadMembers();
    } catch (err) {
      toast({ severity: 'error', message: extractError(err) });
    }
  }, [groupId, toast, t, loadMembers]);

  // Ruolo backend (ADMINISTRATOR/USER) -> etichetta admin/standard.
  const roleLabel = (role: string | null) =>
    t(`userSettings.usersTeam.roles.${role === 'ADMINISTRATOR' ? 'admin' : 'standard'}`);

  // Stato Liferay -> etichetta attivo/disattivato (INACTIVE = disattivato, tutto il resto attivo).
  const statusLabel = (status: number | null) =>
    t(`userSettings.usersTeam.status.${status === USER_STATUS_INACTIVE ? 'inactive' : 'active'}`);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // La ricerca parte da almeno 3 caratteri, come lo standard delle liste server-side
    // (useServerSearch, minChars=3); sotto soglia mostra tutti gli utenti.
    if (q.length < 3) return users;
    return users.filter(
      (u) =>
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q),
    );
  }, [users, search]);

  // "Esporta tutti gli utenti": CSV di TUTTA la lista (non solo i filtrati).
  function exportCsv() {
    setActionsAnchor(null);
    const header = [
      t('userSettings.usersTeam.fields.firstName'),
      t('userSettings.usersTeam.fields.lastName'),
      t('userSettings.usersTeam.columns.screenName'),
      t('userSettings.usersTeam.fields.email'),
      t('userSettings.usersTeam.fields.role'),
      t('userSettings.usersTeam.columns.status'),
    ];
    const rows = users.map((u) => [u.firstName, u.lastName, u.screenName, u.email, roleLabel(u.role), statusLabel(u.status)]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
    // BOM iniziale per far aprire correttamente gli accenti a Excel.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    // Nome file localizzato con data odierna (YYYY-MM-DD locale), es. utenti_2026-07-01.csv
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    triggerBrowserDownload(blob, `${t('userSettings.usersTeam.exportFilename')}_${dateStr}.csv`);
  }

  const columns: GridColDef<Member>[] = [
    {
      field: 'name',
      headerName: t('userSettings.usersTeam.columns.name'),
      flex: 1,
      // La cella è custom (avatar + nome): il valore ordinabile/filtrabile lo dà valueGetter.
      valueGetter: (_value, row) => `${row.firstName} ${row.lastName}`.trim(),
      renderCell: (params) => {
        const m = params.row;
        const initials = (m.lastName?.charAt(0) || m.firstName?.charAt(0) || '?').toUpperCase();
        return (
          <span className="users-settings__name-cell">
            <Avatar
              src={resolvePortraitURL(m.portraitURL)}
              alt={`${m.firstName} ${m.lastName}`}
              sx={{ width: 32, height: 32 }}
            >
              {initials}
            </Avatar>
            <span>{m.firstName} {m.lastName}</span>
          </span>
        );
      },
    },
    { field: 'screenName', headerName: t('userSettings.usersTeam.columns.screenName'), flex: 1 },
    {
      field: 'email',
      headerName: t('userSettings.usersTeam.columns.email'),
      flex: 1.5,
      minWidth: 280,
      renderCell: (params) => {
        const email = params.row.email;
        if (!email) return null;
        return (
          <span className="users-settings__email-cell">
            <span className="users-settings__email-text">{email}</span>
            <IconButton
              size="small"
              aria-label={t('common.copy')}
              className="users-settings__email-copy"
              onClick={async () => {
                await navigator.clipboard.writeText(email);
                toast({ severity: 'success', message: t('common.copied') });
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        );
      },
    },
    {
      field: 'role',
      headerName: t('userSettings.usersTeam.columns.role'),
      width: 180,
      // Ordino per etichetta tradotta (Amministratore/Standard), non per il valore grezzo.
      valueGetter: (_value, row) => roleLabel(row.role),
      renderCell: (params) => roleLabel(params.row.role),
    },
    // TS-139: in sola lettura, si modifica dalla modale come gli altri dati dell'utente.
    {
      field: 'externalReferenceCode',
      headerName: t('userSettings.usersTeam.columns.externalCode'),
      width: 150,
      valueGetter: (_value, row) => row.externalReferenceCode ?? '',
    },
    {
      field: 'status',
      headerName: t('userSettings.usersTeam.columns.status'),
      width: 110,
      // Ordino per etichetta tradotta, non per il valore numerico grezzo.
      valueGetter: (_value, row) => statusLabel(row.status),
      renderCell: (params) => (
        <Chip
          label={statusLabel(params.row.status)}
          size="small"
          variant="outlined"
          color={params.row.status === USER_STATUS_INACTIVE ? 'error' : 'success'}
        />
      ),
    },
    // Azione di modifica riservata agli amministratori CRM (enforce reale lato server).
    // Stile allineato agli altri DataGrid (contatti/aziende): link testuale, non icona.
    ...(isAdmin
      ? [{
          field: 'actions',
          headerName: t('userSettings.usersTeam.columns.actions'),
          width: 200,
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            const inactive = params.row.status === USER_STATUS_INACTIVE;
            const isSelf = params.row.userId === currentUserId;
            return (
              <>
                <Button onClick={() => setModal({ member: params.row })} variant="text" size="small">
                  {t('common.edit')}
                </Button>
                {/* Disattivazione di sé stessi vietata lato server: nascondo il toggle sulla propria riga. */}
                {!isSelf && (
                  <Button
                    onClick={() => {
                      if (inactive) {
                        void handleToggleStatus(params.row);
                      } else {
                        confirm.requestConfirm(
                          t('userSettings.usersTeam.deactivateConfirm', {
                            name: `${params.row.firstName} ${params.row.lastName}`,
                          }),
                          () => void handleToggleStatus(params.row),
                        );
                      }
                    }}
                    variant="text"
                    size="small"
                    color={inactive ? 'success' : 'error'}
                  >
                    {t(inactive
                      ? 'userSettings.usersTeam.reactivate'
                      : 'userSettings.usersTeam.deactivate')}
                  </Button>
                )}
              </>
            );
          },
        } satisfies GridColDef<Member>]
      : []),
  ];

  return (
    <div className="users-settings__panel">
      <p className="users-settings__intro">{t('userSettings.usersTeam.intro')}</p>

      <div className="users-settings__toolbar">
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('userSettings.usersTeam.searchPlaceholder')}
          className="users-settings__search"
        />
        <div className="users-settings__toolbar-actions">
          {/* "Azioni" contiene solo l'export CSV, riservato agli amministratori CRM. */}
          {isAdmin && (
            <Button variant="outlined" onClick={(e) => setActionsAnchor(e.currentTarget)}>
              {t('userSettings.usersTeam.actions')}
            </Button>
          )}
          {isAdmin && (
            <Button variant="contained" onClick={() => setModal({ member: null })}>
              {t('userSettings.usersTeam.addUser')}
            </Button>
          )}
        </div>
      </div>

      <Menu
        anchorEl={actionsAnchor}
        open={Boolean(actionsAnchor)}
        onClose={() => setActionsAnchor(null)}
      >
        <MenuItem onClick={exportCsv}>{t('userSettings.usersTeam.exportAll')}</MenuItem>
      </Menu>

      <DataGrid
        rows={filtered}
        columns={columns}
        getRowId={(r) => r.userId}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
        }}
        className="datagrid-standard"
      />

      {modal && (
        <UserFormModal
          groupId={groupId}
          member={modal.member}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            void loadMembers();
          }}
        />
      )}

      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
        confirmLabel={t('userSettings.usersTeam.deactivate')}
      />
    </div>
  );
}

// ── Modale utente (creazione + modifica, endpoint upsert) ───────────────────
const USER_FORM_ID = 'user-form';

function UserFormModal(
  { groupId, member, onClose, onSaved }:
    { groupId: number; member: Member | null; onClose: () => void; onSaved: () => void },
) {
  const { t } = useTranslation();
  const toast = useToast();
  // member != null -> modifica: precompilo gli state dai dati esistenti.
  const editing = member != null;
  const [firstName, setFirstName] = useState(member?.firstName ?? '');
  const [lastName, setLastName] = useState(member?.lastName ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [role, setRole] = useState<UserRole>(member?.role === 'ADMINISTRATOR' ? 'admin' : 'standard');
  const [externalCode, setExternalCode] = useState(member?.externalReferenceCode ?? '');
  const [saving, setSaving] = useState(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit =
    firstName.trim().length > 0 && lastName.trim().length > 0 && emailValid && !saving;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        emailAddress: email.trim(),
        role: role === 'admin' ? 'ADMINISTRATOR' : 'USER',
        // Scelta single-site: sovrascrivo le membership col solo sito corrente.
        memberships: [{ groupId }],
      };
      const saved = editing
        ? await userApi.updateUser({ ...payload, userId: member.userId })
        : await userApi.createUser(payload);
      // Nome/ruolo modificati (o nuovo utente) devono riflettersi negli
      // autocomplete (cache getGroupUsers).
      userApi.invalidateCache(groupId);
      // TS-139 — Il codice gestionale ha un endpoint suo, chiamato solo se è cambiato. L'utente
      // a questo punto è già salvato: se il codice viene rifiutato (già di un altro utente) lo si
      // dice e si chiude lo stesso, perché rimandare la modale ricreerebbe l'utente appena creato.
      const code = externalCode.trim();
      if (code !== (member?.externalReferenceCode ?? '')) {
        try {
          await userApi.updateExternalReferenceCode(saved.userId, code);
        } catch (err) {
          toast({ severity: 'error', message: extractError(err) });
          onSaved();
          return;
        }
      }
      toast({ severity: 'success', message: t('common.saved') });
      onSaved();
    } catch (err) {
      toast({ severity: 'error', message: extractError(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">
          {t(editing ? 'userSettings.usersTeam.editTitle' : 'userSettings.usersTeam.addTitle')}
        </span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <form id={USER_FORM_ID} onSubmit={handleSubmit}>
          <TextField
            label={t('userSettings.usersTeam.fields.firstName')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            fullWidth
            className="form-field"
          />
          <TextField
            label={t('userSettings.usersTeam.fields.lastName')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            fullWidth
            className="form-field"
          />
          <TextField
            type="email"
            label={t('userSettings.usersTeam.fields.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            className="form-field"
            error={email.length > 0 && !emailValid}
            helperText={email.length > 0 && !emailValid ? t('userSettings.profile.emailInvalid') : undefined}
          />
          <TextField
            select
            label={t('userSettings.usersTeam.fields.role')}
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            fullWidth
            className="form-field"
          >
            <MenuItem value="admin">{t('userSettings.usersTeam.roles.admin')}</MenuItem>
            <MenuItem value="standard">{t('userSettings.usersTeam.roles.standard')}</MenuItem>
          </TextField>
          <TextField
            label={t('userSettings.usersTeam.fields.externalCode')}
            value={externalCode}
            onChange={(e) => setExternalCode(e.target.value)}
            helperText={t('userSettings.usersTeam.fields.externalCodeHint')}
            fullWidth
            className="form-field"
          />
          {/* Nota sulla password temporanea: pertinente solo alla creazione. */}
          {!editing && (
            <p className="text-xs text-muted">{t('userSettings.usersTeam.emailNote')}</p>
          )}
        </form>
      </DialogContent>
      <DialogActions className="app-dialog__footer">
        <Button type="submit" form={USER_FORM_ID} variant="contained" disabled={!canSubmit}>
          {t(editing ? 'common.save' : 'common.create')}
        </Button>
        <Button type="button" onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Tab Team (lista team di sito + crea/modifica/elimina + gestione membri) ──
function TeamsTab() {
  const { t } = useTranslation();
  const toast = useToast();
  const groupId = useCurrentGroupId();
  // Creazione/modifica/eliminazione team e gestione membri: riservate agli amministratori CRM
  // (enforce reale lato server via Permission Framework: MANAGE_TEAMS sul sito).
  const isAdmin = useAppSelector((s) => s.auth.user?.administrator) ?? false;
  const confirm = useConfirmDialog();
  const [search, setSearch] = useState('');
  // null = modale chiusa; { team: null } = creazione; { team } = modifica.
  const [modal, setModal] = useState<{ team: Team | null } | null>(null);
  // Modale gestione membri (solo su un team esistente).
  const [membersModal, setMembersModal] = useState<{ team: Team } | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadTeams = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const list = await teamApi.getGroupTeams(groupId);
      if (mountedRef.current) setTeams(list);
    } catch (err) {
      if (mountedRef.current) toast({ severity: 'error', message: extractError(err) });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [groupId, toast]);

  useEffect(() => { void loadTeams(); }, [loadTeams]);

  const handleDelete = useCallback((team: Team) => {
    confirm.requestConfirm(
      t('userSettings.usersTeam.team.deleteConfirm', { name: team.name }),
      async () => {
        try {
          await teamApi.deleteTeam(team.teamId);
          toast({ severity: 'success', message: t('common.saved') });
          void loadTeams();
        } catch (err) {
          toast({ severity: 'error', message: extractError(err) });
        }
      },
    );
  }, [confirm, t, toast, loadTeams]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    // La ricerca parte da almeno 3 caratteri, come lo standard delle liste server-side
    // (useServerSearch, minChars=3); sotto soglia mostra tutti i team. Filtra su nome e descrizione.
    if (q.length < 3) return teams;
    return teams.filter(
      (tm) =>
        tm.name.toLowerCase().includes(q) ||
        tm.description.toLowerCase().includes(q),
    );
  }, [teams, search]);

  const columns: GridColDef<Team>[] = [
    { field: 'name', headerName: t('userSettings.usersTeam.team.columns.name'), flex: 1 },
    {
      field: 'description',
      headerName: t('userSettings.usersTeam.team.columns.description'),
      flex: 1.5,
    },
    {
      field: 'members',
      headerName: t('userSettings.usersTeam.team.columns.members'),
      width: 120,
      // Cella = numero membri; valueGetter per ordinamento/filtro coerenti.
      valueGetter: (_value, row) => row.members.length,
    },
    // "Gestisci membri" è visibile a tutti: per i non-admin apre la modale in sola
    // lettura (vedi canManage in TeamMembersModal). Modifica ed eliminazione team
    // restano riservate agli amministratori CRM (enforce reale lato server).
    {
      field: 'actions',
      headerName: t('userSettings.usersTeam.team.columns.actions'),
      width: isAdmin ? 320 : 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <Button onClick={() => setMembersModal({ team: params.row })} variant="text" size="small">
            {t('userSettings.usersTeam.team.manageMembers')}
          </Button>
          {isAdmin && (
            <>
              <Button onClick={() => setModal({ team: params.row })} variant="text" size="small">
                {t('common.edit')}
              </Button>
              <Button onClick={() => handleDelete(params.row)} variant="text" size="small" color="error">
                {t('userSettings.usersTeam.team.delete')}
              </Button>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="users-settings__panel">
      <p className="users-settings__intro">{t('userSettings.usersTeam.team.intro')}</p>

      <div className="users-settings__toolbar">
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('userSettings.usersTeam.team.searchPlaceholder')}
          className="users-settings__search"
        />
        <div className="users-settings__toolbar-actions">
          {isAdmin && (
            <Button variant="contained" onClick={() => setModal({ team: null })}>
              {t('userSettings.usersTeam.team.addTeam')}
            </Button>
          )}
        </div>
      </div>

      <DataGrid
        rows={filtered}
        columns={columns}
        getRowId={(r) => r.teamId}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
        }}
        className="datagrid-standard"
      />

      {modal && (
        <TeamFormModal
          groupId={groupId}
          team={modal.team}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            void loadTeams();
          }}
        />
      )}

      {membersModal && (
        <TeamMembersModal
          groupId={groupId}
          team={membersModal.team}
          canManage={isAdmin}
          onClose={() => {
            setMembersModal(null);
            // La lista team mostra il conteggio membri: rifetch per aggiornarlo.
            void loadTeams();
          }}
        />
      )}

      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
        confirmLabel={t('userSettings.usersTeam.team.delete')}
      />
    </div>
  );
}

// ── Modale team (creazione + modifica, endpoint upsert) ─────────────────────
const TEAM_FORM_ID = 'team-form';

function TeamFormModal(
  { groupId, team, onClose, onSaved }:
    { groupId: number; team: Team | null; onClose: () => void; onSaved: () => void },
) {
  const { t } = useTranslation();
  const toast = useToast();
  const editing = team != null;
  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [saving, setSaving] = useState(false);

  const canSubmit = name.trim().length > 0 && !saving;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      if (editing) {
        await teamApi.updateTeam({ teamId: team.teamId, name: name.trim(), description: description.trim() });
      } else {
        await teamApi.createTeam({ groupId, name: name.trim(), description: description.trim() });
      }
      toast({ severity: 'success', message: t('common.saved') });
      onSaved();
    } catch (err) {
      toast({ severity: 'error', message: extractError(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">
          {t(editing ? 'userSettings.usersTeam.team.editTitle' : 'userSettings.usersTeam.team.addTitle')}
        </span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <form id={TEAM_FORM_ID} onSubmit={handleSubmit}>
          <TextField
            label={t('userSettings.usersTeam.team.fields.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            className="form-field"
          />
          <TextField
            label={t('userSettings.usersTeam.team.fields.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            className="form-field"
          />
        </form>
      </DialogContent>
      <DialogActions className="app-dialog__footer">
        <Button type="submit" form={TEAM_FORM_ID} variant="contained" disabled={!canSubmit}>
          {t(editing ? 'common.save' : 'common.create')}
        </Button>
        <Button type="button" onClick={onClose} variant="outlined">
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Modale gestione membri del team (aggiungi dagli utenti del sito / rimuovi) ──
function TeamMembersModal(
  { groupId, team, canManage, onClose }:
    { groupId: number; team: Team; canManage: boolean; onClose: () => void },
) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [members, setMembers] = useState<Member[]>(team.members);
  const [siteUsers, setSiteUsers] = useState<Member[]>([]);
  // Testo digitato nell'Autocomplete: controllato per azzerarlo dopo ogni aggiunta.
  const [addInput, setAddInput] = useState('');
  const [busy, setBusy] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Ruolo backend (ADMINISTRATOR/USER) -> etichetta admin/standard.
  const roleLabel = (role: string | null) =>
    t(`userSettings.usersTeam.roles.${role === 'ADMINISTRATOR' ? 'admin' : 'standard'}`);

  const fullName = (m: Member) => `${m.firstName} ${m.lastName}`.trim();
  const initials = (m: Member) =>
    (m.lastName?.charAt(0) || m.firstName?.charAt(0) || '?').toUpperCase();

  // Utenti del sito per il picker "aggiungi membro" (un team può contenere solo
  // membri del sito). In sola lettura (non-admin) il picker non c'è: salto il fetch.
  useEffect(() => {
    if (!canManage) return;
    void (async () => {
      try {
        const list = await userApi.getGroupMembers(groupId);
        if (mountedRef.current) setSiteUsers(list);
      } catch (err) {
        if (mountedRef.current) toast({ severity: 'error', message: extractError(err) });
      }
    })();
  }, [groupId, canManage, toast]);

  // Utenti del sito non ancora membri del team (candidati all'aggiunta).
  const addableUsers = useMemo(() => {
    const memberIds = new Set(members.map((m) => m.userId));
    return siteUsers.filter((u) => !memberIds.has(u.userId));
  }, [siteUsers, members]);

  // Ordinamento allineato al tab Utenti: per "nome cognome" ascendente
  // (colonna `name` del DataGrid). Il backend (getTeamUsers) non ordina.
  const sortedMembers = useMemo(
    () =>
      [...members].sort((a, b) =>
        `${a.firstName} ${a.lastName}`.trim().localeCompare(`${b.firstName} ${b.lastName}`.trim()),
      ),
    [members],
  );

  // Aggiunta immediata alla selezione dall'Autocomplete (azione non distruttiva:
  // nessuna conferma). Il campo si azzera subito dopo.
  async function handleAdd(user: Member) {
    if (busy) return;
    setBusy(true);
    try {
      const updated = await teamApi.addMember(team.teamId, user.userId);
      if (mountedRef.current) setMembers(updated.members);
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: extractError(err) });
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }

  function handleRemove(m: Member) {
    confirm.requestConfirm(
      t('userSettings.usersTeam.team.members.removeConfirm', { name: fullName(m) }),
      async () => {
        setBusy(true);
        try {
          const updated = await teamApi.removeMember(team.teamId, m.userId);
          if (mountedRef.current) setMembers(updated.members);
          toast({ severity: 'success', message: t('common.saved') });
        } catch (err) {
          toast({ severity: 'error', message: extractError(err) });
        } finally {
          if (mountedRef.current) setBusy(false);
        }
      },
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">
          {t('userSettings.usersTeam.team.members.title', { name: team.name })}
        </span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        {/* Picker ricercabile: aggiunge il membro alla selezione, poi si azzera.
            Solo per amministratori; in sola lettura la modale mostra solo l'elenco. */}
        {canManage && (
          <Autocomplete<Member>
            options={addableUsers}
            getOptionLabel={fullName}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            // value sempre null: la selezione è un'azione (add), non uno stato persistente.
            value={null}
            inputValue={addInput}
            onInputChange={(_e, v) => setAddInput(v)}
            onChange={(_e, user) => {
              if (user) {
                void handleAdd(user);
                setAddInput('');
              }
            }}
            disabled={addableUsers.length === 0 || busy}
            fullWidth
            className="users-settings__member-add"
            renderOption={(props, u) => {
              const { key, ...liProps } = props;
              return (
                <li key={key} {...liProps}>
                  <span className="users-settings__name-cell">
                    <Avatar
                      src={resolvePortraitURL(u.portraitURL)}
                      alt={fullName(u)}
                      sx={{ width: 28, height: 28 }}
                    >
                      {initials(u)}
                    </Avatar>
                    <span className="users-settings__member-info">
                      <span className="users-settings__member-name">{fullName(u)}</span>
                      <span className="users-settings__member-email">{u.email}</span>
                    </span>
                  </span>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('userSettings.usersTeam.team.members.add')}
                placeholder={t('userSettings.usersTeam.team.members.searchToAdd')}
                helperText={addableUsers.length === 0 ? t('userSettings.usersTeam.team.members.noneToAdd') : undefined}
              />
            )}
          />
        )}

        {/* Lista membri correnti */}
        <p className="users-settings__member-count">
          {t('userSettings.usersTeam.team.members.count', { count: members.length })}
        </p>
        {members.length === 0 ? (
          <p className="text-muted">{t('userSettings.usersTeam.team.members.empty')}</p>
        ) : (
          <ul className="users-settings__member-list">
            {sortedMembers.map((m) => {
              const isAdminRole = m.role === 'ADMINISTRATOR';
              return (
                <li key={m.userId} className="users-settings__member-row">
                  <Avatar
                    src={resolvePortraitURL(m.portraitURL)}
                    alt={fullName(m)}
                    sx={{ width: 32, height: 32 }}
                  >
                    {initials(m)}
                  </Avatar>
                  <span className="users-settings__member-info">
                    <span className="users-settings__member-name">{fullName(m)}</span>
                    <span className="users-settings__member-email">{m.email}</span>
                  </span>
                  <Chip
                    label={roleLabel(m.role)}
                    size="small"
                    variant="outlined"
                    color={isAdminRole ? 'primary' : 'default'}
                  />
                  {/* Rimozione membro solo per amministratori (sola lettura per gli altri). */}
                  {canManage && (
                    <Tooltip title={t('userSettings.usersTeam.team.members.remove')}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={t('userSettings.usersTeam.team.members.remove')}
                          onClick={() => handleRemove(m)}
                          disabled={busy}
                        >
                          <PersonRemoveIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>

      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
        confirmLabel={t('userSettings.usersTeam.team.members.remove')}
      />
    </Dialog>
  );
}